import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search, SlidersHorizontal, X, Sparkles, ChevronDown } from "lucide-react";
import Navbar from "@/components/Navbar";
import ExpertCard from "@/components/ExpertCard";
import { API } from "@/context/AuthContext";
import { toast } from "sonner";

const INDUSTRIES = ["Technology", "Banking & Finance", "Healthcare", "Manufacturing", "Retail & Consumer", "Education", "Consulting", "Startups & Entrepreneurship"];
const EXPERTISE = ["Leadership", "Strategy", "Sales & Marketing", "Human Resources", "Finance & Investment", "Technology & Product", "Operations", "Entrepreneurship"];
const SORT_OPTIONS = [
  { value: "featured", label: "Featured" },
  { value: "rating", label: "Highest Rated" },
  { value: "newest", label: "Recently Joined" },
  { value: "price_low", label: "Price: Low to High" },
  { value: "price_high", label: "Price: High to Low" },
];

export default function ExpertDiscovery() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [experts, setExperts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [aiRecs, setAiRecs] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const query = searchParams.get("q") || "";
  const industry = searchParams.get("industry") || "";
  const expertise = searchParams.get("expertise") || "";
  const sort = searchParams.get("sort") || "featured";
  const page = parseInt(searchParams.get("page") || "1");

  const [localQuery, setLocalQuery] = useState(query);

  useEffect(() => {
    fetchExperts();
  }, [searchParams]);

  const fetchExperts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort, page });
      if (query) params.set("q", query);
      if (industry) params.set("industry", industry);
      if (expertise) params.set("expertise", expertise);
      const res = await fetch(`${API}/experts?${params}`);
      if (res.ok) {
        const data = await res.json();
        setExperts(data.experts || []);
        setTotal(data.total || 0);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams(searchParams);
    if (localQuery) p.set("q", localQuery);
    else p.delete("q");
    p.set("page", "1");
    setSearchParams(p);
  };

  const handleAiRecommend = async () => {
    if (!query) {
      toast.error("Enter what you're looking for to get AI recommendations");
      return;
    }
    setAiLoading(true);
    try {
      const res = await fetch(`${API}/ai/recommend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query, limit: 3 }),
      });
      if (res.ok) {
        const data = await res.json();
        setAiRecs(data.recommendations || []);
        if (data.recommendations?.length === 0) toast.info("No AI matches found. Try a more specific query.");
      }
    } finally {
      setAiLoading(false);
    }
  };

  const setFilter = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value);
    else p.delete(key);
    p.set("page", "1");
    setSearchParams(p);
  };

  const clearFilters = () => {
    setSearchParams({});
    setLocalQuery("");
    setAiRecs([]);
  };

  const activeFilters = [
    query && { key: "q", label: `"${query}"` },
    industry && { key: "industry", label: industry },
    expertise && { key: "expertise", label: expertise },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Navbar />
      <div className="pt-20 pb-16">
        {/* Header */}
        <div className="bg-white border-b border-[#E4E4E1] py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-3xl font-normal text-[#0F172A] mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
              Find Your Council
            </h1>
            <form onSubmit={handleSearch} className="flex gap-0 max-w-2xl">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                <input
                  type="text"
                  placeholder="Search expertise, industry, or name..."
                  value={localQuery}
                  onChange={(e) => setLocalQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-[#E4E4E1] border-r-0 bg-white text-sm focus:outline-none focus:border-[#0F172A] rounded-l-sm"
                  data-testid="discovery-search-input"
                />
              </div>
              <button type="submit" className="bg-[#0F172A] text-white px-6 py-3 text-sm font-semibold hover:bg-[#1E293B] transition-colors rounded-r-sm" data-testid="discovery-search-btn">
                Search
              </button>
              <button
                type="button"
                onClick={handleAiRecommend}
                disabled={aiLoading}
                className="ml-2 flex items-center gap-2 border border-[#064E3B] text-[#064E3B] px-4 py-3 text-sm font-semibold hover:bg-[#064E3B] hover:text-white transition-colors rounded-sm"
                data-testid="ai-recommend-btn"
              >
                <Sparkles size={14} />
                {aiLoading ? "Thinking..." : "AI Match"}
              </button>
            </form>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          {/* Filters row */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 border border-[#E4E4E1] bg-white text-sm text-[#0F172A] px-4 py-2 rounded-sm hover:border-[#0F172A] transition-colors"
              data-testid="filters-toggle"
            >
              <SlidersHorizontal size={14} /> Filters
            </button>

            <select
              value={sort}
              onChange={(e) => setFilter("sort", e.target.value)}
              className="border border-[#E4E4E1] bg-white text-sm text-[#0F172A] px-3 py-2 rounded-sm focus:outline-none focus:border-[#0F172A]"
              data-testid="sort-select"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>

            {activeFilters.map((f) => (
              <span key={f.key} className="flex items-center gap-1 bg-[#0F172A] text-white text-xs px-3 py-1.5 rounded-sm">
                {f.label}
                <button onClick={() => setFilter(f.key, "")} className="ml-1 hover:text-white/70">
                  <X size={12} />
                </button>
              </span>
            ))}

            {activeFilters.length > 0 && (
              <button onClick={clearFilters} className="text-xs text-[#52525B] hover:text-[#0F172A] transition-colors underline">
                Clear all
              </button>
            )}

            <span className="ml-auto text-xs text-[#A1A1AA]">{total} experts found</span>
          </div>

          {/* Filter panel */}
          {showFilters && (
            <div className="bg-white border border-[#E4E4E1] rounded-sm p-5 mb-6 grid md:grid-cols-2 gap-6" data-testid="filters-panel">
              <div>
                <p className="text-xs font-semibold text-[#0F172A] mb-2 uppercase tracking-wider">Industry</p>
                <div className="flex flex-wrap gap-2">
                  {INDUSTRIES.map((ind) => (
                    <button
                      key={ind}
                      onClick={() => setFilter("industry", industry === ind ? "" : ind)}
                      className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${industry === ind ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-[#52525B] border-[#E4E4E1] hover:border-[#0F172A]"}`}
                      data-testid={`filter-industry-${ind.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      {ind}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-[#0F172A] mb-2 uppercase tracking-wider">Expertise</p>
                <div className="flex flex-wrap gap-2">
                  {EXPERTISE.map((exp) => (
                    <button
                      key={exp}
                      onClick={() => setFilter("expertise", expertise === exp ? "" : exp)}
                      className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${expertise === exp ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-[#52525B] border-[#E4E4E1] hover:border-[#0F172A]"}`}
                    >
                      {exp}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* AI Recommendations */}
          {aiRecs.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles size={16} className="text-[#064E3B]" />
                <p className="text-sm font-semibold text-[#0F172A]">AI-Matched Experts</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-2">
                {aiRecs.map((rec) => (
                  <div key={rec.user_id} className="relative">
                    <div className="absolute -top-2 left-4 z-10 bg-[#064E3B] text-white text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm">
                      AI Match {rec.match_score}%
                    </div>
                    <ExpertCard expert={rec} />
                    {rec.ai_reason && (
                      <p className="text-xs text-[#52525B] mt-2 px-1 italic">{rec.ai_reason}</p>
                    )}
                  </div>
                ))}
              </div>
              <hr className="border-[#E4E4E1] my-6" />
            </div>
          )}

          {/* Expert grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[...Array(6)].map((_, i) => (
                <div key={`skeleton-${i}`} className="bg-white border border-[#E4E4E1] rounded-sm overflow-hidden animate-pulse">
                  <div className="h-48 bg-[#F4F4F1]" />
                  <div className="p-5 space-y-3">
                    <div className="h-4 bg-[#F4F4F1] rounded w-3/4" />
                    <div className="h-3 bg-[#F4F4F1] rounded w-full" />
                    <div className="h-3 bg-[#F4F4F1] rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : experts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[#52525B] text-sm mb-4">No experts found matching your criteria.</p>
              <button onClick={clearFilters} className="btn-secondary text-sm">Clear Filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 stagger-children" data-testid="experts-grid">
              {experts.map((expert) => (
                <ExpertCard key={expert.user_id} expert={expert} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {total > 12 && (
            <div className="flex justify-center gap-2 mt-10">
              {page > 1 && (
                <button onClick={() => setFilter("page", String(page - 1))} className="btn-secondary text-sm px-4 py-2">
                  Previous
                </button>
              )}
              {page * 12 < total && (
                <button onClick={() => setFilter("page", String(page + 1))} className="btn-primary text-sm px-4 py-2" data-testid="load-more-btn">
                  Next Page
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
